import { ENVS, REGIONS, Env, Region } from '@adsk/acs-sc-web-platform-api';
import Badge from '@adsk/alloy-react-badge';

import { ItemState, Priority } from './types';

export const ASSIGNEE_PICKER_COMPANY = 'company';
export const ASSIGNEE_PICKER_ROLE = 'role';
export const ASSIGNEE_PICKER_USER = 'user';

export const SUBMITTAL_ENTITIY_TYPE = 'submittalitem';
export const SUBMITTAL_ENTITIY_DOMAIN = 'autodesk-construction-submittals';

const US_CONFIG: Record<Env, Record<string, string>> = {
  [ENVS.QA]: {
    baseAddress: 'https://developer-stg.api.autodesk.com/',
    listItemsPath:
      'submittals-qa/{version}/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals-qa/{version}/projects/{projectId}/specs',
    buildFE: 'https://acc-qa.autodesk.com/build',
  },
  [ENVS.STAGING]: {
    baseAddress: 'https://developer-stg.api.autodesk.com/',
    listItemsPath:
      'submittals/{version}/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals/{version}/projects/{projectId}/specs',
    buildFE: 'https://acc-staging.autodesk.com/build',
  },
  [ENVS.PRODUCTION]: {
    baseAddress: 'https://developer.api.autodesk.com/',
    listItemsPath:
      'submittals/{version}/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals/{version}/projects/{projectId}/specs',
    buildFE: 'https://acc.autodesk.com/build',
  },
};

const EMEA_CONFIG: Record<Env, Record<string, string>> = {
  ...US_CONFIG,
  [ENVS.PRODUCTION]: {
    ...US_CONFIG[ENVS.PRODUCTION],
    buildFE: 'https://acc.autodesk.eu/build',
  },
};

const APAC_CONFIG: Record<Env, Record<string, string>> = {
  ...US_CONFIG,
  [ENVS.STAGING]: {
    ...US_CONFIG[ENVS.STAGING],
    buildFE: 'https://acc-staging.apac.autodesk.com/build',
  },
  [ENVS.PRODUCTION]: {
    ...US_CONFIG[ENVS.PRODUCTION],
    buildFE: 'https://acc.apac.autodesk.com/build',
  },
};

const AUS_CONFIG: Record<Env, Record<string, string>> = {
  ...US_CONFIG,
  [ENVS.STAGING]: {
    ...US_CONFIG[ENVS.STAGING],
    buildFE: 'https://acc-staging.aus.autodesk.com/build',
  },
  [ENVS.PRODUCTION]: {
    ...US_CONFIG[ENVS.PRODUCTION],
    buildFE: 'https://acc.aus.autodesk.com/build',
  },
};

const US_CONFIG_V2: Record<Env, Record<string, string>> = {
  [ENVS.QA]: {
    baseAddress: 'https://developer-stg.api.autodesk.com/',
    listItemsPath: 'submittals-qa/v2/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals-qa/v2/projects/{projectId}/specs',
    buildFE: 'https://acc-qa.autodesk.com/build',
  },
  [ENVS.STAGING]: {
    baseAddress: 'https://developer-stg.api.autodesk.com/',
    listItemsPath: 'submittals/v2/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals/v2/projects/{projectId}/specs',
    buildFE: 'https://acc-staging.autodesk.com/build',
  },
  [ENVS.PRODUCTION]: {
    baseAddress: 'https://developer.api.autodesk.com/',
    listItemsPath: 'submittals/v2/projects/{projectId}/items?include=steps',
    listSpecsPath: 'submittals/v2/projects/{projectId}/specs',
    buildFE: 'https://acc.autodesk.com/build',
  },
};

const EMEA_CONFIG_V2: Record<Env, Record<string, string>> = {
  ...US_CONFIG_V2,
  [ENVS.PRODUCTION]: {
    ...US_CONFIG_V2[ENVS.PRODUCTION],
    buildFE: 'https://acc.autodesk.eu/build',
  },
};

const APAC_CONFIG_V2: Record<Env, Record<string, string>> = {
  ...US_CONFIG_V2,
  [ENVS.STAGING]: {
    ...US_CONFIG_V2[ENVS.STAGING],
    buildFE: 'https://acc-staging.apac.autodesk.com/build',
  },
  [ENVS.PRODUCTION]: {
    ...US_CONFIG_V2[ENVS.PRODUCTION],
    buildFE: 'https://acc.apac.autodesk.com/build',
  },
};

const AUS_CONFIG_V2: Record<Env, Record<string, string>> = {
  ...US_CONFIG_V2,
  [ENVS.STAGING]: {
    ...US_CONFIG_V2[ENVS.STAGING],
    buildFE: 'https://acc-staging.aus.autodesk.com/build',
  },
  [ENVS.PRODUCTION]: {
    ...US_CONFIG_V2[ENVS.PRODUCTION],
    buildFE: 'https://acc.aus.autodesk.com/build',
  },
};

export const ENV_CONFIGS: Record<Region, typeof US_CONFIG> = {
  [REGIONS.US]: US_CONFIG,
  [REGIONS.EMEA]: EMEA_CONFIG,
  [REGIONS.APAC]: APAC_CONFIG,
  [REGIONS.AUS]: AUS_CONFIG,
};

export const ENV_CONFIGS_V2: Record<Region, typeof US_CONFIG> = {
  [REGIONS.US]: US_CONFIG_V2,
  [REGIONS.EMEA]: EMEA_CONFIG_V2,
  [REGIONS.APAC]: APAC_CONFIG_V2,
  [REGIONS.AUS]: AUS_CONFIG_V2,
};

export const PAGE_SIZE = 50;

export const VERSION_1 = 'v1';
export const VERSION_2 = 'v2';

export const SUBMITTALS_ROLES_COMPANY_FF = 'submittals-roles-and-companies-fe';

export const REVIEWER_ASSIGNEE_TYPE: Record<string, string> = {
  user: '1',
  company: '2',
};

interface StatusMetadata {
  name: string;
  color: string;
  statusText: string;
  stateText?: string;
}
export const REVIEW_STATE = 'rev';
export const IN_PROGRESS_STEP = 'in-progress';

export const ASSIGNEE_TYPES: Record<string, string> = {
  USER: 'user',
  COMPANY: 'company',
  ROLE: 'role',
};

export const statuses: Record<ItemState, StatusMetadata> = {
  draft: {
    name: 'draft',
    statusText: 'STATUS_DRAFT',
    color: Badge.COLORS.GRAY,
  },
  'sbc-1': {
    name: 'required',
    stateText: 'STATE_SBC1',
    statusText: 'STATUS_REQUIRED',
    color: Badge.COLORS.BLUE,
  },
  'mgr-1': {
    name: 'open',
    stateText: 'STATE_MGR1',
    statusText: 'STATUS_OPEN',
    color: Badge.COLORS.YELLOW,
  },
  rev: {
    name: 'rev',
    stateText: 'STATE_REV',
    statusText: 'STATUS_OPEN',
    color: Badge.COLORS.YELLOW,
  },
  'mgr-2': {
    name: 'mgr-2',
    stateText: 'STATE_MGR2',
    statusText: 'STATUS_OPEN',
    color: Badge.COLORS.YELLOW,
  },
  'sbc-2': {
    name: 'sbc-2',
    statusText: 'STATUS_CLOSED',
    color: Badge.COLORS.GRAY_200,
  },
  void: {
    name: 'void',
    statusText: 'STATUS_VOID',
    color: Badge.COLORS.DEFAULT,
  },
};

// export const priorities = ['High', 'Normal', 'Low'];

interface PriorityData {
  label: string;
  value: string;
}

export const priorities: Record<Priority, PriorityData> = {
  High: {
    label: 'HIGH',
    value: 'High',
  },
  Normal: {
    label: 'NORMAL',
    value: 'Normal',
  },
  Low: {
    label: 'LOW',
    value: 'Low',
  },
};

export const SORT_ORDER = {
  ASC: 'asc',
  DESC: 'desc',
};

export const VIRTUAL_ROW_HEIGHT = 48;

export const VIRTUAL_OVER_SCAN = 3;

export const ENTITIES_MAPPER_KEY = 'id';

export const TABLE_COLUMN_ACCESSOR_KEY = {
  ID: 'id',
  STEPS: 'steps',
  IDENTIFIER: 'identifier',
  STATE_ID: 'stateId',
  REVISION: 'revision',
  TITLE: 'title',
  BALL_IN_COURT_USERS: 'ballInCourtUsers',
  BALL_IN_COURT: 'ballInCourt',
  DUE_DATE: 'dueDate',
  PACKAGE: 'package',
  CUSTOM_IDENTIFIER: 'customIdentifierHumanReadable',
  SPEC: 'spec',
  PRIORITY: 'priority',
};

export const TABLE_COLUMN_ID_KEY = {
  SPEC: 'spec.identifier',
  PACKAGE: 'package.title',
  CUSTOM_IDENTIFIER: 'customIdentifier',
  SELECT: 'select',
};
